import { useMemo, useState } from 'react';
import { KeyRound, Plus, Search } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { Button, Input, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useAccessKeys,
  useCreateAccessKey,
  useUpdateAccessKeyStatus,
  useDeleteAccessKey,
  s3ExtractError,
  type AccessKey,
  type NewAccessKey,
} from '@/api/s3';
import { S3ConsoleBreadcrumbs } from '../S3ConsoleBreadcrumbs';
import { accessKeyColumns } from './access-key-columns';
import { AccessKeySecretDialog } from './AccessKeySecretDialog';

/** Access keys — list, create (secret shown once), enable/disable and delete. */
export function AccessKeyList({ serviceId }: { serviceId: string }) {
  const toast = useToast();
  const { data = [], isLoading, isError, refetch } = useAccessKeys(serviceId);
  const create = useCreateAccessKey(serviceId);
  const updateStatus = useUpdateAccessKeyStatus(serviceId);
  const del = useDeleteAccessKey(serviceId);

  const [secret, setSecret] = useState<NewAccessKey | null>(null);
  const [toDelete, setToDelete] = useState<AccessKey | null>(null);
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.filter((k) => k.access_key_id.toLowerCase().includes(q)) : data;
  }, [data, search]);

  const onCreate = () => {
    create.mutate(undefined, {
      onSuccess: (result) => setSecret(result),
      onError: (e) => toast.error(s3ExtractError(e, 'Failed to create access key')),
    });
  };

  const onToggle = (key: AccessKey) => {
    const next = key.status === 'Active' ? 'Inactive' : 'Active';
    updateStatus.mutate(
      { accessKeyId: key.access_key_id, status: next },
      {
        onSuccess: () => toast.success(next === 'Active' ? 'Key enabled' : 'Key disabled'),
        onError: (e) => toast.error(s3ExtractError(e, 'Failed to update key')),
      },
    );
  };

  const confirmDelete = () => {
    if (!toDelete) return;
    del.mutate(toDelete.access_key_id, {
      onSuccess: () => {
        toast.success('Access key deleted');
        setToDelete(null);
      },
      onError: (e) => {
        toast.error(s3ExtractError(e, 'Failed to delete access key'));
        setToDelete(null);
      },
    });
  };

  const columns = accessKeyColumns({ onToggle, onDelete: setToDelete });

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<S3ConsoleBreadcrumbs trail={[{ label: 'Access Keys' }]} />}
        title={`Access Keys${data.length ? ` (${data.length})` : ''}`}
        description="S3 access keys for the root account of this service."
        actions={
          <Button
            variant="primary"
            startIcon={<Plus size={15} />}
            disabled={create.isPending}
            onClick={onCreate}
          >
            {create.isPending ? 'Creating…' : 'Create Access Key'}
          </Button>
        }
      />

      <Toolbar
        start={
          <Input
            placeholder="Search access keys…"
            startSlot={<Search size={15} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Couldn't load access keys"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          The storage service is temporarily unavailable.
        </Alert>
      ) : isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={44} />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => r.access_key_id}
          emptyState={
            data.length === 0 ? (
              <EmptyState
                icon={<KeyRound size={20} />}
                title="No access keys yet"
                description="Create an access key to connect S3-compatible clients."
                actions={
                  <Button size="sm" startIcon={<Plus size={14} />} onClick={onCreate}>
                    Create Access Key
                  </Button>
                }
              />
            ) : (
              'No access keys match your search.'
            )
          }
        />
      )}

      <AccessKeySecretDialog secret={secret} onClose={() => setSecret(null)} />

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => !o && setToDelete(null)}
        title="Delete Access Key"
        description={`Applications using "${toDelete?.access_key_id}" will lose access immediately. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={confirmDelete}
      />
    </Container>
  );
}
